import { makeVar } from '@apollo/client';
import { useMemo } from 'react';
import { useQueryParams } from 'src/helpers/history';
import {
  ProcurementTabInfo,
  ProcurementsDisplay,
  OverviewQp,
  CallOffsSubCategory
} from 'src/models/procurements/types';
import { useActiveMpIdState, useLoadAllMpsIds } from 'src/models/matchingProfiles/hooks';
import { useActiveWsIdState, useWorkspacesIds } from 'src/models/workspace/hooks';
import { isProcurementDisplay, isCallOffSubCategory } from 'src/models/procurements/helpers';
import { Paths } from 'src/pages/paths';
import { useLoadInboxesNav } from 'src/models/callOffs/hooks';
import { FeatureFlag, useFeatureFlag } from 'src/helpers/featureFlag';
import { useLocation } from 'react-use';

export const OverviewModeVar = makeVar<ProcurementTabInfo>(undefined);
export function useOverviewMode(): ProcurementTabInfo | undefined {
  const { pathname, search } = useLocation();
  const qp = useQueryParams(search);
  const [activeMpId] = useActiveMpIdState();
  const [activeWsId] = useActiveWsIdState();
  const { loading: mpsDataLoading } = useLoadAllMpsIds();
  const { loading: wssDataLoading } = useWorkspacesIds();
  const isCallOffFeature = useFeatureFlag(FeatureFlag.Mercury);
  const { loading: inboxesDataLoading } = useLoadInboxesNav(!isCallOffFeature);
  const isMonitoringProfilesFeature = useFeatureFlag(FeatureFlag.MonitoringProfiles);

  const isNotOverview = pathname && Paths.PROCUREMENT_TABLE_PAGE.split('/')[1] !== pathname.split('/')[1];
  const overviewMode = useMemo((): ProcurementTabInfo | undefined => {
    if (isNotOverview) return;
    const qpTab = isProcurementDisplay(qp[OverviewQp.Tab]) ? qp[OverviewQp.Tab] : ProcurementsDisplay.MATCHED;
    if (mpsDataLoading || wssDataLoading || inboxesDataLoading) {
      return;
    } else if (qpTab === ProcurementsDisplay.ALL || qpTab === ProcurementsDisplay.MARKED) {
      return { tab: qpTab };
    } else if (qpTab === ProcurementsDisplay.WORKSPACE) {
      if (activeWsId) {
        return {
          tab: ProcurementsDisplay.WORKSPACE,
          workspaceId: activeWsId
        };
      } else {
        return { tab: ProcurementsDisplay.WORKSPACE, workspaceId: '' };
      }
    } else if (qpTab === ProcurementsDisplay.ARCHIVED_WORKSPACES) {
      return { tab: ProcurementsDisplay.ARCHIVED_WORKSPACES };
    } else if (qpTab === ProcurementsDisplay.WORKSPACE_CREATING) {
      return { tab: ProcurementsDisplay.WORKSPACE_CREATING };
    } else if (qpTab === ProcurementsDisplay.DOCUMENTS_LIBRARY) {
      return { tab: ProcurementsDisplay.DOCUMENTS_LIBRARY };
    } else if (qpTab === ProcurementsDisplay.CALL_OFFS) {
      const qpSubCategory = qp[OverviewQp.SubCategory];
      const subCategory = isCallOffSubCategory(qpSubCategory) ? qpSubCategory : CallOffsSubCategory.NEW;
      return {
        tab: ProcurementsDisplay.CALL_OFFS,
        subCategory: subCategory
      };
    } else {
      return {
        tab: isMonitoringProfilesFeature ? ProcurementsDisplay.MATCHED : ProcurementsDisplay.ALL,
        matchingId: activeMpId
      };
    }
  }, [
    activeMpId,
    activeWsId,
    inboxesDataLoading,
    isMonitoringProfilesFeature,
    isNotOverview,
    mpsDataLoading,
    qp,
    wssDataLoading
  ]);

  return OverviewModeVar(overviewMode);
}

export default useOverviewMode;
